import { Injectable, EventEmitter, Input } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Login } from '../components/login/models/login.model';
import { ErrorLoginService } from './error-login.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  showSideBar = new EventEmitter<boolean>();
  showSideBarUpdateUser = new EventEmitter<boolean>();
  private geoServerUrl: string = environment.geoServerUrl;
  private bifrostUrl: string = environment.bifrostUrl;

  constructor(
    private apollo: Apollo,
    private http: HttpClient,
    private errorLoginService: ErrorLoginService,
  ) {}

  /** Subject que armazena a mensagem de erro do login **/
  private messageErrorSubject: Subject<string> = new Subject<string>();

  /** Observable que escuta a valor recebido pelo subject com a mensagem de erro de login
   *  para que possa ser utilizado no componente de login
   *
   *  **/
  public messageErrorSubject$ = this.messageErrorSubject.asObservable();

  /*
   *
   * @param email
   * @param password
   * @returns
  public logIn(email: string | null, password: string | null) {

    let user: Login = { username: email!, password: password! }

    // A rota de login agora é /login/ ao invés de /graphql/
    let bifrostLoginUrl = this.bifrostUrl.replace('graphql', 'login')

    return this.http.post(`${bifrostLoginUrl}`, JSON.stringify(user), {
    }).pipe(
      catchError((error: any) => {

        // Variável que armazena o erro retornado pela api
        let errorMessage: any = '';

        // Salva o caminho que contem a mensagem do erro
        errorMessage = error.error.errors[0].message;

        // Atualiza o subject com o erro já tratado
        this.messageErrorSubject.next(this.errorLoginService.errorLoginLib(errorMessage))

        return throwError(() => error);
      })
    );
  }
   */

  /**
   * Primeira etapa do login (usuário e senha).
   * Resposta sem 2FA: { id, token, companies, ... }
   * Resposta com 2FA: { mfaRequired: true, mfaToken }
   */
  public logIn(email: string | null, password: string | null): Observable<any> {
    let user: Login = { username: email!, password: password! };

    return this.http
      .post(this.getLoginUrl(), JSON.stringify(user), {})
      .pipe(catchError((error: any) => this.handleLoginError(error)));
  }

  /**
   * Segunda etapa do login: valida o código de 6 dígitos do app ou um código de recuperação.
   * Em caso de sucesso devolve o mesmo payload do login sem 2FA.
   */
  public verifyMfa(
    mfaToken: string,
    code?: string | null,
    recoveryCode?: string | null,
  ): Observable<any> {
    const body: any = { mfaToken };
    if (recoveryCode) {
      body.recoveryCode = recoveryCode;
    } else {
      body.code = code;
    }

    return this.http
      .post(`${this.getLoginUrl()}2fa/`, JSON.stringify(body), {})
      .pipe(catchError((error: any) => this.handleLoginError(error)));
  }

  /** A rota de login é /login/ ao invés de /graphql/ (a URL do bifrost termina com barra). */
  private getLoginUrl(): string {
    return this.bifrostUrl.replace('graphql', 'login');
  }

  /** Publica a mensagem de erro já traduzida e repassa o erro para o effect. */
  private handleLoginError(error: any): Observable<never> {
    /** Caminho que contém a mensagem do erro (vazio se não houver corpo, ex.: erro de rede) **/
    const errorMessage: string = error?.error?.errors?.[0]?.message ?? '';

    /** Atualiza o subject com o erro já tratado **/
    this.messageErrorSubject.next(
      this.errorLoginService.errorLoginLib(errorMessage),
    );

    return throwError(() => error);
  }

  // checa se o geoserver pode ser acessado com o username e senha no momento do login
  checkGeoServerAuth(username: string, password: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(`${username}:${password}`),
    });

    return this.http
      .get(`${this.geoServerUrl}geoserver/rest/`, {
        headers: headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error during GeoServer auth check:', error);

          return throwError(() => error);
        }),
      );
  }
}
